type EditableSegment =
  | {
      type: 'text';
      value: string;
      start: number;
      end: number;
    }
  | {
      type: 'mention';
      value: string;
      start: number;
      end: number;
    }
  | {
      type: 'link';
      value: { name: string; href: string };
      start: number;
      end: number;
    };

const linkRegex = /\[([^[\]]+)\]\(([^()\s]+)\)/g;

const escapeRegExp = (token: string) =>
  token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

type MentionPart = { text: string; isMention: boolean };

const splitByMentions = (
  value: string,
  mentionTokens: readonly string[],
): MentionPart[] => {
  if (mentionTokens.length === 0) {
    return [{ text: value, isMention: false }];
  }

  const mentionRegex = new RegExp(
    mentionTokens.map(escapeRegExp).join('|'),
    'g',
  );
  const parts: MentionPart[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = mentionRegex.exec(value)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        text: value.slice(lastIndex, match.index),
        isMention: false,
      });
    }
    parts.push({ text: match[0], isMention: true });
    lastIndex = mentionRegex.lastIndex;
  }

  if (lastIndex < value.length || parts.length === 0) {
    parts.push({ text: value.slice(lastIndex), isMention: false });
  }

  return parts;
};

export const getMentionTriggerPosition = (
  text: string,
  cursorPosition: number,
  mentionTokens: readonly string[],
): number => {
  if (mentionTokens.length === 0) return -1;

  const textBeforeCursor = text.slice(0, cursorPosition);
  const lastAt = textBeforeCursor.lastIndexOf('@');
  if (lastAt === -1) return -1;

  const typed = textBeforeCursor.slice(lastAt).toLowerCase();
  const letters = typed.slice(1);
  const matchesToken = mentionTokens.some((token) =>
    token.toLowerCase().startsWith(typed),
  );

  if (!letters || matchesToken) {
    return lastAt;
  }

  return -1;
};

export const parseEditable = (
  value: string,
  mentionTokens: readonly string[] = [],
): EditableSegment[] => {
  const segments: EditableSegment[] = [];
  const mentionParts = splitByMentions(value, mentionTokens);
  let cursor = 0;

  mentionParts.forEach((part) => {
    if (part.isMention) {
      if (segments.length === 0 && cursor === 0) {
        segments.push({ type: 'text', value: '', start: 0, end: 0 });
      }
      segments.push({
        type: 'mention',
        value: part.text,
        start: cursor,
        end: cursor + part.text.length,
      });
      cursor += part.text.length;
      return;
    }

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    linkRegex.lastIndex = 0;
    while ((match = linkRegex.exec(part.text)) !== null) {
      const [fullMatch, linkName, linkHref] = match;
      if (match.index > lastIndex) {
        segments.push({
          type: 'text',
          value: part.text.slice(lastIndex, match.index),
          start: cursor + lastIndex,
          end: cursor + match.index,
        });
      } else if (match.index === 0 && segments.length === 0 && cursor === 0) {
        segments.push({ type: 'text', value: '', start: 0, end: 0 });
      }

      segments.push({
        type: 'link',
        value: { name: linkName, href: linkHref },
        start: cursor + match.index,
        end: cursor + match.index + fullMatch.length,
      });
      lastIndex = match.index + fullMatch.length;
    }

    segments.push({
      type: 'text',
      value: part.text.slice(lastIndex),
      start: cursor + lastIndex,
      end: cursor + part.text.length,
    });

    cursor += part.text.length;
  });

  const coalesced: EditableSegment[] = [];
  segments.forEach((seg) => {
    const last = coalesced[coalesced.length - 1];
    if (last && last.type === 'text' && seg.type === 'text') {
      last.value += seg.value;
      last.end = seg.end;
    } else {
      coalesced.push({ ...seg });
    }
  });

  if (coalesced.length === 0) {
    coalesced.push({ type: 'text', value: '', start: 0, end: 0 });
  }

  return coalesced;
};

export const findSegmentAt = (
  segments: EditableSegment[],
  position: number,
): EditableSegment | null => {
  for (let i = 0; i < segments.length; i += 1) {
    const seg = segments[i];
    if (
      i > 0 &&
      position === seg.start &&
      segments[i - 1].type === 'text' &&
      segments[i - 1].start === segments[i - 1].end
    ) {
      return segments[i - 1];
    }
    if (seg.type === 'text' && seg.start === seg.end) {
      if (position === seg.start && i < segments.length - 1) {
        const next = segments[i + 1];
        if (position <= next.end) {
          return next;
        }
      }
      continue;
    }
    if (position < seg.start) continue;
    if (position < seg.end) {
      return seg;
    }
  }
  const last = segments[segments.length - 1];
  if (last && position === last.end) return last;
  return null;
};

export const renderedOffsetToMarkdownOffset = (
  segments: EditableSegment[],
  renderedOffset: number,
): number => {
  let renderedPos = 0;

  for (const seg of segments) {
    const renderedLength =
      seg.type === 'link' ? seg.value.name.length : seg.value.length;

    if (renderedOffset <= renderedPos + renderedLength) {
      const offsetInSeg = renderedOffset - renderedPos;
      if (seg.type === 'link') {
        if (offsetInSeg >= renderedLength) {
          return seg.end;
        }
        return seg.start + 1 + offsetInSeg;
      }
      return seg.start + offsetInSeg;
    }

    renderedPos += renderedLength;
  }

  const last = segments[segments.length - 1];
  return last ? last.end : renderedOffset;
};

export const markdownOffsetToRenderedOffset = (
  segments: EditableSegment[],
  mdOffset: number,
): number => {
  let renderedPos = 0;
  for (const seg of segments) {
    const renderedLength =
      seg.type === 'link' ? seg.value.name.length : seg.value.length;

    if (mdOffset <= seg.start) {
      return renderedPos;
    }
    if (mdOffset >= seg.end) {
      renderedPos += renderedLength;
      continue;
    }

    const offsetInSeg = mdOffset - seg.start;
    if (seg.type === 'link') {
      const nameStart = 1;
      const nameEnd = nameStart + seg.value.name.length;
      if (offsetInSeg <= nameStart) return renderedPos;
      if (offsetInSeg >= nameEnd) return renderedPos + seg.value.name.length;
      return renderedPos + (offsetInSeg - nameStart);
    }

    return renderedPos + offsetInSeg;
  }
  return renderedPos;
};
